import { ComponentType, memo, useMemo } from 'react';
import { makeStyles, Theme, Typography } from '@material-ui/core';
import { SvgIconProps } from '@material-ui/core/SvgIcon';
import { MarkdownContent } from '@backstage/core-components';
import { pullRequestUrl } from '../../links';
import { emojify } from './emoji';
import InfoIcon from '@material-ui/icons/Info';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import AnnouncementIcon from '@material-ui/icons/Announcement';
import WarningIcon from '@material-ui/icons/Warning';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';

type AlertType = 'NOTE' | 'TIP' | 'IMPORTANT' | 'WARNING' | 'CAUTION';

type Segment =
  | { kind: 'md'; text: string }
  | { kind: 'alert'; type: AlertType; text: string };

// GitHub alert blockquote opener: `> [!IMPORTANT]` (optionally with text after).
const ALERT_OPEN = /^>\s*\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]\s?(.*)$/i;

// Code-fence delimiter (``` or ~~~). Fenced content (e.g. a terraform plan or
// a markdown example) must pass through untouched — rewriting inside a fence
// (or extracting a quoted alert out of one) leaves an unclosed fence that
// swallows the rest of the body.
const FENCE_RE = /^\s*(```|~~~)/;

// The release-note body hides each PR title inside an HTML comment, leaving only
// "#<n> by @user" visible (e.g. `- <!-- feat(x): … --> #123 by @y`). Reveal the
// title so the changelog is readable, and linkify the PR number. Only list items
// are touched; structural placeholder comments (e.g. "Release note starts") stay
// hidden.
const CHANGELOG_ITEM = /^(\s*[-*]\s+)<!--\s*([\s\S]*?)\s*-->\s*(.*)$/;
function revealChangelogLine(line: string): string {
  const m = line.match(CHANGELOG_ITEM);
  if (!m) return line;
  const [, bullet, title, rest] = m;
  const text = title.trim() !== '' ? `${title.trim()} ${rest}` : rest;
  const linked = text.replace(
    /#(\d+)\b/g,
    (_full, n) => `[#${n}](${pullRequestUrl(n)})`,
  );
  return `${bullet}${linked}`.replace(/\s+$/, '');
}

// Line-level rewrites (changelog title reveal + emoji shortcodes), skipping
// fenced code blocks.
function transformBody(md: string): string {
  let inFence = false;
  return md
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map(line => {
      if (FENCE_RE.test(line)) {
        inFence = !inFence;
        return line;
      }
      if (inFence) return line;
      return emojify(revealChangelogLine(line));
    })
    .join('\n');
}

// Split markdown into GitHub-style alert blocks and plain markdown runs. An
// alert is a blockquote whose first line is `[!TYPE]` (outside code fences);
// its body is the rest of the blockquote (de-quoted). Everything else is
// passed through as markdown.
function parseSegments(md: string): Segment[] {
  const lines = md.split('\n');
  const segments: Segment[] = [];
  let buf: string[] = [];
  let inFence = false;
  const flush = () => {
    if (buf.join('').trim() !== '') {
      segments.push({ kind: 'md', text: buf.join('\n') });
    }
    buf = [];
  };
  for (let i = 0; i < lines.length; i++) {
    if (FENCE_RE.test(lines[i])) {
      inFence = !inFence;
      buf.push(lines[i]);
      continue;
    }
    const m = inFence ? null : lines[i].match(ALERT_OPEN);
    if (!m) {
      buf.push(lines[i]);
      continue;
    }
    flush();
    const type = m[1].toUpperCase() as AlertType;
    const body: string[] = [];
    if (m[2].trim() !== '') body.push(m[2]);
    let j = i + 1;
    for (; j < lines.length && /^>/.test(lines[j]); j++) {
      body.push(lines[j].replace(/^>\s?/, ''));
    }
    segments.push({ kind: 'alert', type, text: body.join('\n') });
    i = j - 1;
  }
  flush();
  return segments;
}

const ALERT_META: Record<
  AlertType,
  { label: string; color: string; Icon: ComponentType<SvgIconProps> }
> = {
  NOTE: { label: 'Note', color: '#4493f8', Icon: InfoIcon },
  TIP: { label: 'Tip', color: '#3fb950', Icon: EmojiObjectsIcon },
  IMPORTANT: { label: 'Important', color: '#ab7df8', Icon: AnnouncementIcon },
  WARNING: { label: 'Warning', color: '#d29922', Icon: WarningIcon },
  CAUTION: { label: 'Caution', color: '#f85149', Icon: ReportProblemIcon },
};

const useStyles = makeStyles((theme: Theme) => ({
  // Readable markdown body — no fixed height (it expands; the row scrolls if
  // needed) and comfortable spacing.
  root: {
    gridColumn: '1 / -1',
    fontSize: '0.875rem',
    lineHeight: 1.6,
    color: theme.palette.text.primary,
    '& p': { margin: theme.spacing(0.75, 0) },
    '& ul, & ol': {
      margin: theme.spacing(0.75, 0),
      paddingLeft: theme.spacing(3),
    },
    '& li': { margin: theme.spacing(0.25, 0) },
    '& li > p': { margin: 0 },
    '& h1, & h2, & h3, & h4': {
      fontSize: '0.95rem',
      fontWeight: 700,
      margin: theme.spacing(1.25, 0, 0.5),
    },
    '& code': {
      fontSize: '0.85em',
      padding: '0.1em 0.35em',
      borderRadius: 4,
      background:
        theme.palette.type === 'dark'
          ? 'rgba(110, 118, 129, 0.4)'
          : 'rgba(175, 184, 193, 0.2)',
    },
    // Solid, high-contrast code block (terraform plan is rendered here). The
    // previous translucent background left the monospace text hard to read in
    // dark mode; use a solid GitHub-like surface + bright text and scroll wide
    // output horizontally.
    '& pre': {
      padding: theme.spacing(1),
      borderRadius: 6,
      overflow: 'auto',
      fontSize: '0.78rem',
      lineHeight: 1.5,
      border: `1px solid ${theme.palette.divider}`,
      background: theme.palette.type === 'dark' ? '#161b22' : '#f6f8fa',
      color: theme.palette.type === 'dark' ? '#e6edf3' : '#24292f',
    },
    '& pre code': {
      background: 'transparent',
      padding: 0,
      color: 'inherit',
      fontSize: 'inherit',
    },
    '& a': { color: theme.palette.primary.main },
  },
  alert: {
    margin: theme.spacing(1.25, 0),
    padding: theme.spacing(1.5, 2),
    borderLeft: '0.25em solid',
    borderRadius: 6,
  },
  alertTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    fontWeight: 600,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(0.75),
  },
  alertIcon: { fontSize: '1.1rem' },
  // The alert body reuses the same markdown styling as root (paragraphs/links).
  alertBody: {
    '& p': { margin: theme.spacing(0.5, 0) },
    '& p:last-child': { marginBottom: 0 },
  },
}));

const AlertCallout = ({ type, text }: { type: AlertType; text: string }) => {
  const classes = useStyles();
  const meta = ALERT_META[type];
  const { Icon } = meta;
  return (
    <div
      className={classes.alert}
      style={{ borderLeftColor: meta.color, background: `${meta.color}14` }}
    >
      <Typography
        component="div"
        className={classes.alertTitle}
        style={{ color: meta.color }}
      >
        <Icon className={classes.alertIcon} style={{ color: meta.color }} />
        {meta.label}
      </Typography>
      <div className={classes.alertBody}>
        <MarkdownContent content={text} />
      </div>
    </div>
  );
};

/** Renders a PR body as markdown, with GitHub-style `[!TYPE]` alert callouts. */
// Memoized on `body`: descriptions can be large (terraform plans ~26KB) and the
// group page re-renders every second while a build/deploy run is in flight —
// without memo the full parse + remark pipeline would re-run at 1Hz.
export const PrDescription = memo(({ body }: { body: string }) => {
  const classes = useStyles();
  const segments = useMemo(() => parseSegments(transformBody(body)), [body]);
  return (
    <div className={classes.root}>
      {segments.map((seg, i) =>
        seg.kind === 'alert' ? (
          <AlertCallout key={i} type={seg.type} text={seg.text} />
        ) : (
          <MarkdownContent key={i} content={seg.text} />
        ),
      )}
    </div>
  );
});
